import React, { useEffect, useContext, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useLocation, useParams, useHistory } from 'react-router-dom'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import MainTemplate from "../../templates/MainTemplate"
import { Button, List, Typography } from '@material-ui/core'
import CaseList from '../CaseList'
import { GlobalContext } from '../../../hooks/reducer'
import { actions } from "../../../hooks/useLoginUserReducer"
import { actions as shareCaseActions } from "../../../hooks/useShereCaseReducer"
import { useLiveVisibleCases } from '../../../hooks/useLiveVisibleCases'
import CustomModal from "../../common/CustomModal"
import PageTitle from '../../common/PageTitle'
import PageSizeSelector from '../common/PageSizeSelector'

// 時間の設定
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')

const getStyles = makeStyles((theme) => ({
  icon: {
    width: '70px',
    height: '70px',
  },
  cursorPager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '30px',
  }
}))

const FbCase = React.memo((props) => {
  const { companyId } = useParams()
  const classes = getStyles()
  const history = useHistory()
  const location = useLocation()
  const { state: { shareCase, loginUser }, dispatch } = useContext(GlobalContext)
  const {
    inputComments, caseComments
  } = shareCase
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [pageCases, setPageCases] = useState([])
  const [hasNextPage, setHasNextPage] = useState(false)
  const [pageCursors, setPageCursors] = useState([null])
  const [isLoadingCases, setIsLoadingCases] = useState(true)
  const currentCursor = pageCursors[page - 1] || null
  const baseVisibleCases = useMemo(() => pageCases, [pageCases])
  const visibleCases = useLiveVisibleCases(baseVisibleCases, loginUser.isLogin)
  const commentsByCaseKey = useMemo(() => {
    return caseComments.reduce((acc, comment) => {
      if (!acc[comment.caseKey]) acc[comment.caseKey] = []
      acc[comment.caseKey].push(comment)
      return acc
    }, {})
  }, [caseComments])

  // 初回mount
  useEffect(() => {
    // ログインしていない場合、ログインページに遷移
    if (!loginUser.isLogin) { actions.loginCheck(dispatch, history) }
    if (loginUser.isLogin) {
      shareCaseActions.getMasterData(dispatch)
      shareCaseActions.getCompany(dispatch, companyId)
    }
    return () => { dispatch({ type: 'COMMON_RESET' }) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, history, loginUser.isLogin])

  useEffect(() => {
    if (!loginUser.isLogin) return undefined

    const visibleCaseKeys = visibleCases.map((content) => content.key)
    // コメント・いいねはフィードバック対象の事例だけを購読する。
    const cleanupComments = shareCaseActions.watchVisibleComments(dispatch, visibleCaseKeys)
    const cleanupGoods = shareCaseActions.watchVisibleGoods(dispatch, loginUser.user.uid, visibleCaseKeys)

    return () => {
      cleanupComments()
      cleanupGoods()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, loginUser.isLogin, loginUser.user.uid, visibleCases])

  useEffect(() => {
    if (!loginUser.isLogin) return undefined

    let cancelled = false
    setIsLoadingCases(true)

    shareCaseActions.getCasesPage({ perPage, cursorKey: currentCursor, companyId, feedbackOnly: true })
      .then(({ cases: nextCases, hasNext, nextCursor }) => {
        if (cancelled) return

        setPageCases(nextCases)
        setHasNextPage(hasNext)
        setPageCursors((prev) => {
          const next = prev.slice(0, page)

          if (hasNext && nextCursor) {
            next[page] = nextCursor
          }

          return next
        })
      })
      .catch((error) => {
        if (cancelled) return
        console.error('getFeedbackCasesPage error', error)
        setPageCases([])
        setHasNextPage(false)
      })
      .finally(() => {
        if (cancelled) return
        setIsLoadingCases(false)
      })

    return () => {
      cancelled = true
    }
  }, [companyId, currentCursor, loginUser.isLogin, page, perPage])

  useEffect(() => {
    setPage(1)
    setPageCases([])
    setHasNextPage(false)
    setPageCursors([null])
  }, [companyId])

  if (!companyId) { return <MainTemplate></MainTemplate> }

  const handlePerPageChange = (nextPerPage) => {
    if (nextPerPage === perPage) return

    setPerPage(nextPerPage)
    setPage(1)
    setPageCases([])
    setHasNextPage(false)
    setPageCursors([null])
  }

  const renderPager = () => {
    if (pageCases.length <= 0 || isLoadingCases) return null

    return (
      <div className={classes.cursorPager}>
        <Button
          variant="outlined"
          color="primary"
          disabled={page <= 1}
          onClick={() => setPage((prevPage) => Math.max(1, prevPage - 1))}
        >
          前へ
        </Button>

        <Typography variant="body2">
          {page}ページ目
        </Typography>

        <Button
          variant="contained"
          color="primary"
          disabled={!hasNextPage}
          onClick={() => setPage((prevPage) => prevPage + 1)}
        >
          次へ
        </Button>

        <Button
          variant="text"
          color="primary"
          disabled={page <= 1}
          onClick={() => {
            setPageCases([])
            setHasNextPage(false)
            setPageCursors([null])
            setPage(1)
          }}
        >
          最新へ戻る
        </Button>
      </div>
    )
  }

  return (
    <MainTemplate>

      {/* モーダル */}
      <CustomModal />
      <PageTitle title='フィードバック' />

      <PageSizeSelector perPage={perPage} onChange={handlePerPageChange} />

      {renderPager()}

      <List className={classes.list}>
        <React.Fragment>
          {
            visibleCases.length <= 0 ? (
              isLoadingCases ? '読み込み中...' : 'フィードバックがありません。'
            ) : (
              visibleCases.map((content) => {
                const comments = commentsByCaseKey[content.key] || []
                return <CaseList
                  hash={location.hash}
                  key={content.key}
                  content={content}
                  inputComment={inputComments[content.key]}
                  comments={comments}
                  handleDispatch={(e) => dispatch(e)}
                />
              })
            )
          }
        </React.Fragment>
      </List>

      {renderPager()}

    </MainTemplate >
  )
})
export default FbCase
